import { Observable, tap } from 'rxjs';
import {
  AbstractControl,
  AbstractControlOptions as NgAbstractControlOptions,
  AsyncValidatorFn,
  FormGroup as NgFormGroup,
  ValidatorFn,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';

import { enableControl, enableValidators } from './forms.utils';

type ReactiveControlOptions<TContext = unknown> = NgAbstractControlOptions & {
  readonly context?: TContext;
  readonly destroyRef: DestroyRef;
};

export type AbstractControlOptions<TContext = unknown> = NgAbstractControlOptions | ReactiveControlOptions<TContext>;

function isReactiveControlOptions<TContext = unknown>(validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions<TContext> | null): validatorOrOpts is ReactiveControlOptions<TContext> {
  return validatorOrOpts != null && typeof validatorOrOpts !== 'function' && !Array.isArray(validatorOrOpts) && 'destroyRef' in validatorOrOpts;
}

export abstract class ReactiveFormGroup<TControl extends {
  [K in keyof TControl]: AbstractControl;
} = any, TContext = unknown> extends NgFormGroup<TControl> {
  context: TContext | undefined;

  public constructor(controls: TControl, validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions<TContext> | null, asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null) {
    super(controls, validatorOrOpts, asyncValidator);
    if (isReactiveControlOptions<TContext>(validatorOrOpts)) {
      this.context = validatorOrOpts.context;
      this.startMonitoring(validatorOrOpts);
    }
  }

  public override enable(opts: { onlySelf?: boolean, emitEvent?: boolean } = {}): void {
    super.enable({ emitEvent: false, onlySelf: true });
    this.onMonitor(this.context, opts);
  }

  public override disable(opts: { onlySelf?: boolean, emitEvent?: boolean } = {}): void {
    super.disable({ emitEvent: false, onlySelf: true });
    this.onMonitor(this.context, opts);
  }

  protected abstract woMonitor(): Observable<unknown>;

  protected abstract woDoCheck(context: TContext | undefined, opts: { onlySelf: boolean, emitEvent: boolean }): void;

  protected woSetValue<TValue>(
    control: AbstractControl<TValue>,
    value: TValue,
    opts: { onlySelf: boolean, emitEvent: boolean } = { emitEvent: false, onlySelf: true },
  ): this {
    control.setValue(value, opts);
    return this;
  }

  protected woEnableControl(
    control: AbstractControl,
    enabled = true,
    opts: { onlySelf: boolean, emitEvent: boolean } = { emitEvent: false, onlySelf: true },
  ): this {
    enableControl(control, enabled, opts);
    return this;
  }

  protected woEnableValidators(
    control: AbstractControl,
    validators: ValidatorFn | ValidatorFn[],
    enabled = true,
    opts: { onlySelf: boolean, emitEvent: boolean } = { emitEvent: false, onlySelf: true },
  ): this {
    enableValidators(control, validators, enabled, opts);
    return this;
  }

  private startMonitoring({ destroyRef, context }: ReactiveControlOptions<TContext>): void {
    this.onMonitor(context);

    this.woMonitor().pipe(
      tap(() => this.onMonitor(context)),
      takeUntilDestroyed(destroyRef),
    ).subscribe();
  }

  private onMonitor(context: TContext | undefined, opts: { onlySelf?: boolean, emitEvent?: boolean } = {}): void {
    this.woDoCheck(context, { emitEvent: false, onlySelf: true });
    this.updateValueAndValidity(opts);
  }
}
